import React, { useEffect, useState } from "react";
import { Icons } from "../icons.jsx";
import { useCreateSkillsAcademyEnrollmentRequest } from "../useSkillsAcademy.js";
import { useAuth } from "../../../contexts/AuthContext";
import { hasEnquired, markEnquired } from "../../../utils/enquiryGuard";
import {
  EnquiryModalBackdrop,
  EnquiryModalPanel,
  EnquiryModalCloseBtn,
  EnquiryModalHeader,
  EnquiryModalProgramTag,
  EnquiryModalTitle,
  EnquiryModalSubtitle,
  EnquiryModalActions,
  EnquiryModalSubmitBtn,
  EnquiryModalSuccess,
  EnquiryModalSuccessTitle,
  EnquiryModalSuccessText,
} from "./style.js";

type Props = {
  program: {
    id: string;
    name: string;
    categories?: { name: string }[];
  };
  onClose: () => void;
  alreadySent?: boolean;
};

const EnquiryModal = ({ program, onClose, alreadySent = false }: Props) => {
  const { mutate, isPending, isSuccess } = useCreateSkillsAcademyEnrollmentRequest();
  const { loggedInUser } = useAuth();
  const userId = (loggedInUser as { id?: string })?.id ?? "";
  const category = program.categories?.[0]?.name ?? "";

  const [alreadyRequested, setAlreadyRequested] = useState(() =>
    alreadySent || hasEnquired(userId, program.id),
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <EnquiryModalBackdrop onClick={onClose}>
      <EnquiryModalPanel onClick={(e) => e.stopPropagation()}>
        <EnquiryModalCloseBtn onClick={onClose}>
          <Icons.x size={18} />
        </EnquiryModalCloseBtn>

        {/* TEMPORARY: show success if already requested via localStorage */}
        {isSuccess || alreadyRequested ? (
          <EnquiryModalSuccess>
            <EnquiryModalSuccessTitle>
              We'll be in touch!
            </EnquiryModalSuccessTitle>
            <EnquiryModalSuccessText>
              Thanks for your interest in {program.name}. We'll reach out with
              more information shortly.
            </EnquiryModalSuccessText>
          </EnquiryModalSuccess>
        ) : (
          <>
            <EnquiryModalHeader>
              {category && (
                <EnquiryModalProgramTag>{category}</EnquiryModalProgramTag>
              )}
              <EnquiryModalTitle>
                Enquire about this programme
              </EnquiryModalTitle>
              <EnquiryModalSubtitle>
                Interested in {program.name}? Click below and we'll get back to
                you with more information.
              </EnquiryModalSubtitle>
              <EnquiryModalActions>
                <EnquiryModalSubmitBtn
                  type="button"
                  disabled={isPending}
                  onClick={() => {
                    mutate({ programId: program.id }, {
                      // TEMPORARY: persist to localStorage on success
                      onSuccess: () => {
                        markEnquired(userId, program.id);
                        setAlreadyRequested(true);
                      },
                      // END TEMPORARY
                    });
                  }}
                >
                  {isPending ? "Sending…" : "Send enquiry"}
                </EnquiryModalSubmitBtn>
              </EnquiryModalActions>
            </EnquiryModalHeader>
          </>
        )}
      </EnquiryModalPanel>
    </EnquiryModalBackdrop>
  );
};

export default EnquiryModal;
